import filters from '@app/filters/global';
import AppStoreModule from '@app/store/modules/AppStoreModule';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import IDateRange from '@app/types/base/IDateRange';
import ILocation from '@app/types/base/ILocation';
import IOperatingTime, { IAnyOperatingTime, ILabeledOperatingTimes } from '@app/types/base/IOperatingTime';
import IRegion from '@app/types/base/IRegion';
import ITrain from '@app/types/base/ITrain';
import { OperatingTimeType } from '@app/types/base/OperatingTimeType';
import SeasonType from '@app/types/base/SeasonType';
import IConnection from '@app/types/data/base/IConnection';
import IConnectionPriceGroup from '@app/types/data/base/IConnectionPriceGroup';
import IConnectionPriceGroupItem from '@app/types/data/base/IConnectionPriceGroupItem';
import IFlatConnection from '@app/types/data/base/IFlatConnection';
import IImage from '@app/types/data/base/IImage';
import IMapLabel from '@app/types/data/base/IMapLabel';
import ISeasonWithPrice from '@app/types/data/base/ISeasonWithPrice';
import ISlideData from '@app/types/data/slides/ISlideData';
import ISlideTransform from '@app/types/ISlideTransform';
import ISlideMaster, { isSlideMaster } from '@app/types/presentations/master/ISlideMaster';
import IPresentationSettings from '@app/types/presentations/presentation/IPresentationSettings';
import { documentToHtmlString, Next, Options } from '@contentful/rich-text-html-renderer';
import { Block, BLOCKS, Document, Inline, INLINES, MARKS } from '@contentful/rich-text-types';
import ICloudinaryAsset from '@libraries/cloudinary/ICloudinaryAsset';
import { Cloudinary, Transformation as CloudinaryTransformation } from 'cloudinary-core';
import { Entry } from 'contentful';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import IVideo from '../../types/data/base/IVideo';
import IErrorSlideData from '../../types/data/slides/IErrorSlideData';
import CloudinaryTransform from '../CloudinaryTransform';

const cloudinaryInstance = new Cloudinary({
	// eslint-disable-next-line @typescript-eslint/naming-convention
	cloud_name: process.env.CLOUDINARY_CLOUD_NAME
});
const globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);
const appStore: AppStoreModule = getStoreModule(AppStoreModule);

export const thumbnailCropOptions: Partial<CloudinaryTransformation.Options> = {
	width: 320,
	height: 180,
	crop: 'fill'
};
/**
 * Base class for all slide transforms
 * Bundles helpers to be used during slide transforms in child classes
 */
export default abstract class AbstractSlideTransform implements ISlideTransform {
	public abstract viewComponent: string;

	public abstract transform(
		slide: ISlideMaster,
		presentationSettings: IPresentationSettings,
		...rest: any[]
	): Promise<ISlideData>;

	private static mapLabelsByCacheKey: Record<string, IMapLabel[]> = {};

	/**
	 * Transform a richtext field from contentful into html markup
	 * @param richtext
	 * @param presentationSettings
	 */
	/**
	 * Transforms an entry link to a slide into an HTML anchor element
	 *
	 * @param node The content node
	 * @param next Function to process child nodes
	 * @param presentationSettings Current presentation settings
	 */
	protected renderSlideLink(node: Block | Inline, next: Next, presentationSettings: IPresentationSettings): string {
		// links to entries are denoted in a special format that can be parsed by the Richtext component later on
		// the linked slide must be selected in the presentation.
		// plain text is rendered instead of an anchor otherwise.
		const slideIsSelectedInPresentationSettings = presentationSettings.slides.find(
			(slide) => slide.selected && slide.id === node.data.target.sys.id
		);

		if (slideIsSelectedInPresentationSettings) {
			return `<a href="entry-link::slide::${node.data.target.sys.id}">${next(node.content)}</a>`;
		}

		return next(node.content);
	}

	/**
	 * Transforms an entry link to an externalLink into an HTML anchor element
	 * Optionally includes a favicon if provided in the externalLink
	 *
	 * @param node The content node
	 * @param next Function to process child nodes
	 */
	protected renderExternalLink(node: Block | Inline, next: Next): string {
		try {
			const externalLinkEntry = node.data.target;

			// Validate that required fields exist
			if (!externalLinkEntry.fields || !externalLinkEntry.fields.url) {
				console.warn('External link is missing URL field:', externalLinkEntry);
				return next(node.content);
			}

			const { url } = externalLinkEntry.fields;
			const label = externalLinkEntry.fields.label || next(node.content);

			// Check if there's a valid favicon to display
			if (externalLinkEntry.fields.favicon) {
				try {
					// Transform the favicon asset
					const faviconAsset = externalLinkEntry.fields.favicon;
					const transformedFavicon = this.transformImageAsset(faviconAsset, {
						width: 16,
						height: 16,
						crop: 'fit'
					});

					// Create an anchor with favicon - alle Elemente inline ohne Zeilenumbrüche, um die Textflussdarstellung zu erhalten
					return `<a href="${url}" class="external-link-with-favicon" rel="external" target="_blank" title="${label}"><img src="${
						transformedFavicon.src
					}" alt="" class="external-link-favicon" /><span class="external-link-text">${next(node.content)}</span></a>`;
				} catch (error) {
					console.warn('Failed to transform favicon for external link:', error);
					// Fall back to link without favicon
				}
			}

			// Return an anchor without favicon
			return `<a href="${url}" class="external-link" rel="external" target="_blank" title="${label}"><span class="external-link-text">${next(
				node.content
			)}</span></a>`;
		} catch (error) {
			console.error('Error rendering external link:', error);
			return next(node.content);
		}
	}

	/**
	 * Transform a richtext field from contentful into html markup
	 *
	 * @param richtext Document to transform
	 * @param presentationSettings Current presentation settings
	 */
	protected transformRichtext(richtext: Document, presentationSettings: IPresentationSettings): string {
		const richtTextOptions: Partial<Options> = {
			renderMark: {
				[MARKS.BOLD]: (text: string): string => `<strong>${text}</strong>`
			},
			renderNode: {
				[INLINES.HYPERLINK]: (node: Block | Inline, next: Next): string =>
					`<a href="${node.data.uri}" rel="external" target="_blank">${next(node.content)}</a>`,
				[INLINES.ENTRY_HYPERLINK]: (node: Block | Inline, next: Next): string => {
					try {
						// Check if the linked entry is an externalLink content type
						if (node.data.target?.sys?.contentType?.sys?.id === 'externalLink') {
							return this.renderExternalLink(node, next);
						}

						// Regular slide link handling
						return this.renderSlideLink(node, next, presentationSettings);
					} catch (error) {
						console.error('Error processing entry hyperlink:', error);
						return next(node.content);
					}
				},
				[BLOCKS.PARAGRAPH]: (node: Block | Inline, next: Next): string =>
					`<p>${next(node.content)
						.trim()
						.replace(/\r\n|\r|\n/g, '<br>')}</p>`
			}
		};

		return documentToHtmlString(richtext, richtTextOptions);
	}

	/**
	 * Transforms an asset field from CF and transforms it into an IImage
	 * If transformations have been applied to the asset in CF, these will be applied first
	 * Optional transformations in parameter `transformation`, these will be applied last
	 * @param asset
	 * @param transformation
	 */
	protected transformImageAsset(
		asset: ICloudinaryAsset,
		transformation?: CloudinaryTransformation | CloudinaryTransformation.Options
	): IImage {
		return CloudinaryTransform.transformImageAsset(asset, transformation);
	}

	/**
	 * Provides a fallback image that may be used instead of an image missing in contentful entries
	 * (e.g. to prevent type errors in live previews with incomplete entries)
	 * @protected
	 */
	protected getFallbackImage(): ICloudinaryAsset {
		return {
			url: 'http://media.jungfrau.ch/image/upload/v1565963884/Jungfraujoch-Sphinx-Gletscher-Sonnenuntergang-2.jpg',
			tags: [],
			type: 'upload',
			bytes: 24641954,
			width: 12316,
			format: 'jpg',
			height: 6928,
			version: '1565963884',
			public_id: 'Jungfraujoch-Sphinx-Gletscher-Sonnenuntergang-2',
			created_at: '2019-08-16T13:58:04Z',
			secure_url:
				'https://media.jungfrau.ch/image/upload/v1565963884/Jungfraujoch-Sphinx-Gletscher-Sonnenuntergang-2.jpg',
			resource_type: 'image',
			context: {}
		};
	}

	/**
	 * Transforms an asset field from CF and transforms it into an IVideo
	 * If transformations have been applied to the asset in CF, these will be applied first
	 * Optional transformations in parameter `transformation`, these will be applied last
	 * @param asset
	 * @param transformation
	 * @param posterImage
	 * @param posterTransformation
	 */
	protected transformVideoAsset(
		asset: ICloudinaryAsset,
		transformation?: CloudinaryTransformation | CloudinaryTransformation.Options,
		posterImage?: 'auto' | ICloudinaryAsset,
		posterTransformation?: CloudinaryTransformation | CloudinaryTransformation.Options
	): IVideo {
		return CloudinaryTransform.transformVideoAsset(asset, transformation, posterImage, posterTransformation);
	}

	/**
	 * Provides asset to provide to thumbnail property of ISlideData objects.
	 * Will try to use field `thumbnail` first and then fall back to `fallBackMediaFields` items one by one until an existing asset is found.
	 *
	 * @param alt lat text
	 * @param slide slide to extract asset from
	 * @param fallBackMediaFields field names to fall back to in case field thumbnail has not been populated.
	 */
	protected extractThumbnail(alt: string, slide: ISlideMaster, ...fallBackMediaFields: string[]): IImage {
		let asset: IImage = {
			alt,
			src: cloudinaryInstance.url(
				process.env.LIGHTTABLE_FALLBACK_THUMBNAIL_CLOUDINARY_ID as string,
				CloudinaryTransformation.new(thumbnailCropOptions).toOptions()
			)
		};

		['thumbnail', ...fallBackMediaFields]
			.filter(
				(fallBackMediaField: string): boolean =>
					slide.fields[fallBackMediaField] &&
					((slide.fields[fallBackMediaField].fields && slide.fields[fallBackMediaField].fields.asset) ||
						(slide.fields[fallBackMediaField] as ICloudinaryAsset).public_id)
			)
			.slice(0, 1)
			.forEach((fallBackMediaField: string): void => {
				asset = this.transformImageAsset(
					slide.fields[fallBackMediaField].fields
						? slide.fields[fallBackMediaField].fields.asset
						: slide.fields[fallBackMediaField],
					thumbnailCropOptions
				);
			});

		return asset;
	}

	public async handleTransformError(slide: ISlideMaster, error: Error): Promise<IErrorSlideData> {
		const imageAsset: ICloudinaryAsset = slide.fields.thumbnail
			? slide.fields.thumbnail.fields.asset
			: slide.fields.image.fields.asset;

		return {
			id: slide.id,
			label: slide.fields.label || slide.fields.headline,
			headline: `Error in slide "${slide.fields.label || slide.fields.headline}"`,
			thumbnail: this.transformImageAsset(imageAsset, {
				...thumbnailCropOptions,
				effect: 'grayscale',
				opacity: 30,
				format: 'png'
			}),
			errorMessage: error.message
		};
	}

	public parseConnection(connectionEntry: Entry<any>): IConnection {
		const connection: IConnection = {
			id: connectionEntry.sys.id,
			type: connectionEntry.fields.type as IFlatConnection['type'],
			stations: [],
			priceGroups: [],
			operatingTimes: AbstractSlideTransform.parseObjectWithOperatingTimes(connectionEntry),
			svgId: connectionEntry.fields.svgId as IFlatConnection['svgId'],
			isConnectingTicket: connectionEntry.fields.isConnectingTicket === 'true',
			connectingTicketInfo: connectionEntry.fields.connectingTicketInfo as IFlatConnection['connectingTicketInfo']
		};

		if (connectionEntry.fields.stations) {
			connection.stations = (connectionEntry.fields.stations as Entry[]).map(
				(stationEntry): ILocation => ({
					id: stationEntry.sys.id,
					title: stationEntry.fields.title as string,
					svgId: stationEntry.fields.svgId as string
				})
			);
		}

		if (connectionEntry.fields.seasons) {
			connection.seasons = (connectionEntry.fields.seasons as Entry[]).map(
				(seasonEntry): ISeasonWithPrice => ({
					type: seasonEntry.fields.type as SeasonType,
					title: seasonEntry.fields.title as string,
					startDate: seasonEntry.fields.startDate as string,
					endDate: seasonEntry.fields.endDate as string,
					price: seasonEntry.fields.price as number
				})
			);
		}

		if (connectionEntry.fields.priceGroups) {
			connection.priceGroups = (connectionEntry.fields.priceGroups as Entry[]).map(
				(priceGroupEntry): IConnectionPriceGroup => ({
					comfortClass: priceGroupEntry.fields.comfortClass as IConnectionPriceGroup['comfortClass'],
					priceInfo: priceGroupEntry.fields.priceGroupInfo
						? ((priceGroupEntry.fields.priceGroupInfo as Entry).fields.copy as IConnectionPriceGroup['priceInfo'])
						: (priceGroupEntry.fields.priceInfo as IConnectionPriceGroup['priceInfo']),
					prices: (priceGroupEntry.fields.prices as Entry[]).map(
						(priceEntry: Entry<any>): IConnectionPriceGroupItem => ({
							...(priceEntry.fields as any)
						})
					)
				})
			);
		}

		return connection;
	}

	public static getMapLabels(): IMapLabel[] {
		const cacheKey: string = `${appStore.localeCode}-${
			appStore.systemOfUnits
		}-${globalsStore.entitiesDateUpdated.toString(16)}`;

		if (!AbstractSlideTransform.mapLabelsByCacheKey[cacheKey]) {
			const mapLabels: IMapLabel[] = [
				...globalsStore.locations
					.filter((location: ILocation): boolean => !!location.svgId)
					.map(
						(location: ILocation): IMapLabel => ({
							label: location.title,
							type: 'location',
							svgId: location.svgId as string,
							isSelectable: false,
							image: location.image
								? CloudinaryTransform.transformImageAsset(location.image, {
										width: 300, // eslint-disable-line
										height: 300, // eslint-disable-line
										crop: 'limit' // eslint-disable-line
								  })
								: undefined
						})
					),
				...globalsStore.regions
					.filter((location: IRegion): boolean => !!location.svgId)
					.map(
						(region: IRegion): IMapLabel => ({
							label: region.title,
							type: 'region',
							svgId: region.svgId as string,
							isSelectable: false
						})
					),
				...globalsStore.connections
					.filter(
						(connection: IFlatConnection): boolean =>
							!!connection.svgId && !!(connection.lineLength || connection.duration)
					)
					.map((connection: IFlatConnection): IMapLabel => {
						const labelFragments = [];

						if (connection.lineLength) {
							labelFragments.push(filters.formatDistance((connection.lineLength as number) / 1000));
						}

						if (connection.duration) {
							labelFragments.push(filters.formatDuration(connection.duration as number));
						}

						return {
							label: labelFragments.join(' / '),
							type: 'meta',
							svgId: connection.svgId as string
						};
					}),
				...globalsStore.trains
					.filter((train: ITrain): boolean => !!train.svgId)
					.map(
						(train: ITrain): IMapLabel => ({
							label: train.name,
							type: 'train',
							svgId: train.svgId as string,
							isSelectable: false,
							image: train.image
								? CloudinaryTransform.transformImageAsset(train.image, {
										width: 120, // eslint-disable-line
										height: 154, // eslint-disable-line
										crop: 'limit' // eslint-disable-line
								  })
								: undefined
						})
					)
			];

			const uniqueMapLabels: IMapLabel[] = [];
			mapLabels.forEach((mapLabel: IMapLabel) => {
				const hasSome: boolean = uniqueMapLabels.some(
					(mapLabel2: IMapLabel) => mapLabel.svgId === mapLabel2.svgId && mapLabel.type === mapLabel2.type
				);

				if (!hasSome) {
					uniqueMapLabels.push(mapLabel);
				}
			});

			AbstractSlideTransform.mapLabelsByCacheKey[cacheKey] = uniqueMapLabels;
		}

		return AbstractSlideTransform.mapLabelsByCacheKey[cacheKey];
	}

	private static parseMaintenancePeriod(entry: any): IDateRange {
		return {
			start: new Date(entry.fields.start),
			end: new Date(entry.fields.end)
		};
	}

	private static parseLabeledOperatingTimes(entry: any, type: OperatingTimeType): ILabeledOperatingTimes {
		return {
			label: entry.fields.label,
			operatingTimes: AbstractSlideTransform.parseObjectWithOperatingTimes(entry).map(
				(operatingTime: IOperatingTime | ILabeledOperatingTimes): IOperatingTime => ({ ...operatingTime, type })
			)
		};
	}

	private static parseOperatingTime(entry: any, type: OperatingTimeType): IOperatingTime {
		let period: undefined | IDateRange;

		if (entry.fields.start && entry.fields.end) {
			period = {
				start: new Date(entry.fields.start),
				end: new Date(entry.fields.end)
			};
		}

		let maintenanceWindows: IDateRange[] = [];

		if (entry.fields.maintenancePeriods) {
			maintenanceWindows = entry.fields.maintenancePeriods.map(this.parseMaintenancePeriod);
		}

		return {
			type,
			period,
			maintenanceWindows
		};
	}

	public static parseObjectWithOperatingTimes(entry: any | ISlideMaster): IAnyOperatingTime[] {
		const fields = entry.fields ? entry.fields : entry;

		if (!fields.operatingTimes) {
			return [];
		}

		const operatingTimes = Array.isArray(fields.operatingTimes) ? fields.operatingTimes : [fields.operatingTimes];

		let type: OperatingTimeType = 'other';
		let contentType: string | undefined;

		if (isSlideMaster(entry)) {
			contentType = entry.type;
		} else if (entry.sys && entry.sys.contentType && entry.sys.contentType.sys) {
			contentType = entry.sys.contentType.sys.id;
		}

		if (contentType && ['train', 'connection'].includes(contentType)) {
			type = 'train';
		}

		return operatingTimes.flatMap((operatingTime: any): IAnyOperatingTime[] => {
			const operatingTimeType: string = operatingTime.sys.contentType.sys.id;

			switch (operatingTimeType) {
				case 'train':
					return AbstractSlideTransform.parseObjectWithOperatingTimes(operatingTime);
				case 'labeledOperatingTime':
					return [AbstractSlideTransform.parseLabeledOperatingTimes(operatingTime, type)];
				case 'operatingTime':
				case 'operatingTimePeriod':
					return [AbstractSlideTransform.parseOperatingTime(operatingTime, type)];
				default:
					throw new Error(`Unknown operating time type: ${operatingTimeType}`);
			}
		});
	}

	public static parseOperatingTimes(operatingTimes: unknown, type: OperatingTimeType): IAnyOperatingTime[] {
		if (!Array.isArray(operatingTimes)) {
			return [];
		}

		return operatingTimes.flatMap((operatingTime: any): (IOperatingTime | ILabeledOperatingTimes)[] => {
			let operatingTimeType: string | undefined;

			if (operatingTime && operatingTime.sys && operatingTime.sys.contentType && operatingTime.sys.contentType.sys) {
				operatingTimeType = operatingTime.sys.contentType.sys.id;
			}

			switch (operatingTimeType) {
				case 'train':
					return AbstractSlideTransform.parseObjectWithOperatingTimes(operatingTime);
				case 'labeledOperatingTime':
					return [AbstractSlideTransform.parseLabeledOperatingTimes(operatingTime, type)];
				case 'operatingTime':
				case 'operatingTimePeriod':
					return [AbstractSlideTransform.parseOperatingTime(operatingTime, type)];
				default:
					throw new Error(`Unknown operating time type: ${operatingTimeType}`);
			}
		});
	}
}
